import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, AutoComplete, Button, Card, Col, Form, Input, Radio, Row, Select, Spin, Typography } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import FundingService from '../../service/FundingService'
import SimplePaymentService from '../../service/SimplePaymentService'
import RecentService from '../../service/RecentService'
import WallexService from '../../service/WallexService'
import { FundingCreatePayload } from '../../domain/Funding'
import { useRecentValues } from '../../hooks/useRecent'
import { fillEmpty, fundingSample } from '../../data/samples'
import { paths } from '../../routes'
import { groupThousands } from '../../util/format'
import AmountField from '../ui/AmountField'
import CreatedTicket from '../ui/CreatedTicket'
const { Text } = Typography

const fundingMethodOptions = [
    { value: 'BANK_TRANSFER', label: 'Bank Transfer' },
    { value: 'COLLECTION', label: 'Collection' },
    { value: 'REFUND', label: 'Refund' },
]

const matchesInput = (input: string, option?: { label?: React.ReactNode }) =>
    String(option?.label ?? '')
        .toLowerCase()
        .includes(input.toLowerCase())

const FundingCreateForm: React.FC = () => {
    const [form] = Form.useForm<FundingCreatePayload>()
    const fundingMethod = Form.useWatch('fundingMethod', form)
    const accountOptions = useRecentValues('accountId')
    const navigate = useNavigate()
    const [created, setCreated] = useState<{ id: string; values: FundingCreatePayload } | null>(null)
    const [submitting, setSubmitting] = useState<boolean>(false)
    const [error, setError] = useState<string | null>(null)
    const [simplePaymentOptions, setSimplePaymentOptions] = useState<{ label: string; value: string }[]>([])
    const [searchingPayments, setSearchingPayments] = useState<boolean>(false)
    const isRefund = fundingMethod === 'REFUND'

    const onFinish = (values: FundingCreatePayload) => {
        setSubmitting(true)
        setError(null)
        FundingService.create(values)
            .then((result) => {
                RecentService.record({
                    kind: 'funding',
                    id: result.id,
                    label: typeof values.reference === 'string' ? values.reference : undefined,
                    accountId: values.accountId,
                })
                setCreated({ id: result.id, values })
            })
            .catch((e) => setError(WallexService.errorDescription(e, 'Couldn’t create the funding.')))
            .finally(() => setSubmitting(false))
    }

    // Refunds reference simple payments, looked up by id as the user types
    const searchPayments = (paymentId: string) => {
        if (paymentId.length > 5) {
            setSearchingPayments(true)
            SimplePaymentService.retrieve(paymentId)
                .then((payment) => {
                    setSimplePaymentOptions((options) =>
                        options.some((option) => option.label === payment.id) ? options : [{ label: payment.id, value: payment.wallexRef }, ...options]
                    )
                })
                .catch(() => {})
                .finally(() => {
                    setSearchingPayments(false)
                })
        }
    }

    return (
        <>
            {created && (
                <CreatedTicket
                    noun="funding"
                    id={created.id}
                    details={[
                        { label: 'Amount', value: `${groupThousands(created.values.amount)} ${created.values.currencyId}` },
                        { label: 'Method', value: fundingMethodOptions.find((option) => option.value === created.values.fundingMethod)?.label },
                        { label: 'Account ID', value: created.values.accountId },
                    ]}
                    onUpdateStatus={() => navigate(paths.funding.update(created.id))}
                    onCreateAnother={() => setCreated(null)}
                />
            )}

            {/* Unmounted while the ticket shows; the form instance keeps its values, so "Create another" starts from the previous ones */}
            {!created && (
                <Form
                    form={form}
                    name="funding-create"
                    layout="vertical"
                    initialValues={{ fundingMethod: 'BANK_TRANSFER' }}
                    onFinish={onFinish}
                    onValuesChange={(changed: Partial<FundingCreatePayload>) => {
                        // A reference typed for one method doesn't fit another (refunds take simple payments)
                        if ('fundingMethod' in changed) form.setFieldsValue({ reference: undefined })
                    }}
                >
                    <div className="stack">
                        <div className="form-toolbar">
                            <Text type="secondary">Fields marked * are required.</Text>
                            <Button icon={<ExperimentOutlined />} onClick={() => fillEmpty(form, fundingSample(fundingMethod))}>
                                Fill sample data
                            </Button>
                        </div>

                        <Card title="Funding Information">
                            <Row gutter={16}>
                                <Col xs={24} md={12}>
                                    <Form.Item
                                        label="Account ID"
                                        name="accountId"
                                        rules={[{ required: true, message: 'Please input Account Id' }]}
                                        extra="The account that receives the money"
                                    >
                                        <AutoComplete options={accountOptions} filterOption={matchesInput} placeholder="Enter Account ID" />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} md={12}>
                                    <AmountField label="Funding Amount" currencyName="currencyId" />
                                </Col>
                                <Col xs={24}>
                                    <Form.Item label="Funding Method" name="fundingMethod" rules={[{ required: true, message: 'Please input funding method' }]}>
                                        <Radio.Group optionType="button" buttonStyle="solid" options={fundingMethodOptions} />
                                    </Form.Item>
                                </Col>
                                <Col xs={24}>
                                    <Form.Item
                                        label={isRefund ? 'Simple Payment IDs' : 'Reference'}
                                        name="reference"
                                        rules={[{ required: true, message: isRefund ? 'Please add at least one Simple Payment' : 'Please input Reference' }]}
                                        extra={isRefund ? 'Type a Simple Payment ID (at least 6 characters) to find it' : undefined}
                                    >
                                        {isRefund ? (
                                            <Select
                                                mode="multiple"
                                                showSearch
                                                options={simplePaymentOptions}
                                                placeholder="Search Simple Payment ID"
                                                onSearch={searchPayments}
                                                optionFilterProp="label"
                                                notFoundContent={searchingPayments ? <Spin size="small" /> : null}
                                            />
                                        ) : (
                                            <Input placeholder="e.g. invoice or transfer reference" />
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>
                        </Card>

                        {error && <Alert type="error" showIcon message={error} />}

                        <div className="form-actions">
                            <Button type="primary" size="large" htmlType="submit" loading={submitting}>
                                Create Funding
                            </Button>
                            <Button size="large" onClick={() => form.resetFields()}>
                                Reset
                            </Button>
                        </div>
                    </div>
                </Form>
            )}
        </>
    )
}

export default FundingCreateForm
